import { useId, useState } from 'react';
import type { ReactNode } from 'react';

export interface AccordionItem {
  id: string;
  heading: string;
  content: ReactNode;
  /**
   * Sprungmarke fuer ein Inhaltsverzeichnis darueber.
   *
   * Wahlfrei: Ohne Verzeichnis braucht die Ueberschrift keine Kennung. Mit
   * einem muss sie eine tragen, die von aussen bekannt ist -- die ``useId``
   * dieser Komponente ist es nicht.
   */
  anchor?: string;
}

interface AccordionProps {
  items: AccordionItem[];
  /** Wie viele Abschnitte am Anfang offen stehen -- der Vorgabe nach der erste. */
  openInitially?: number;
}

/**
 * Aufklappbare Abschnitte -- F4-26.
 *
 * **Von Hand und nicht von Radix.** Die Reiter kommen von dort, weil
 * Pfeiltasten, `aria-selected` und die Verbindung Reiter/Inhalt
 * erfahrungsgemaess vergessen werden. Ein Akkordeon ist das nicht wert: Es
 * ist das Aufklapp-Muster der WAI-ARIA-Praxis, und das besteht aus einem
 * Knopf mit `aria-expanded`, der auf einen Bereich zeigt. Dafuer eine
 * Abhaengigkeit aufzunehmen waere mehr Aufwand in der Pflege als im Bau.
 *
 * **Die Ueberschrift steht auch zugeklappt.** Sie ist der Grund, warum es
 * ueberhaupt Abschnitte gibt: Wer eine Anleitung ueberfliegt, sucht
 * "Durchfuehrung" und nicht die dritte Klappe von oben.
 *
 * Jeder Abschnitt haelt seinen eigenen Zustand -- mehrere duerfen offen sein.
 * Ein Akkordeon, das beim Aufklappen des naechsten den vorigen schliesst,
 * macht das Vergleichen zweier Abschnitte unmoeglich, und genau das tut man
 * beim Abarbeiten einer Anleitung.
 *
 * **``openInitially`` ist eine Zahl und kein Schalter**, und die
 * Wissensdatenbank setzt sie auf die Zahl der Abschnitte: Dort steht beim
 * Lesen alles offen. Wer eine Anleitung waehrend eines Telefonats abarbeitet,
 * braucht sie vollstaendig vor sich -- jeder Klick ist eine Hand, die vom
 * Hoerer muss. Zuklappen bleibt trotzdem moeglich; was man abgearbeitet hat,
 * darf aus dem Weg.
 */
export function Accordion({ items, openInitially = 1 }: AccordionProps) {
  const prefix = useId();
  const [open, setOpen] = useState<Set<string>>(
    () => new Set(items.slice(0, openInitially).map((item) => item.id)),
  );

  const toggle = (id: string) => {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="accordion">
      {items.map((item) => {
        const expanded = open.has(item.id);
        const buttonId = `${prefix}-${item.id}-button`;
        const panelId = `${prefix}-${item.id}-panel`;

        return (
          <div key={item.id} className="accordion__item">
            {/* Eine Ueberschrift und kein blanker Knopf: Wer mit einer
                Sprachausgabe von Ueberschrift zu Ueberschrift springt, soll
                die Abschnitte finden, ohne den Text dazwischen zu hoeren. */}
            <h3 className="accordion__heading" id={item.anchor}>
              <button
                type="button"
                id={buttonId}
                className="accordion__trigger"
                aria-expanded={expanded}
                aria-controls={panelId}
                onClick={() => {
                  toggle(item.id);
                }}
              >
                <span className="accordion__marker" aria-hidden="true">
                  {expanded ? '▾' : '▸'}
                </span>
                {item.heading}
              </button>
            </h3>

            <div
              id={panelId}
              role="region"
              aria-labelledby={buttonId}
              className="accordion__panel"
              hidden={!expanded}
            >
              {item.content}
            </div>
          </div>
        );
      })}
    </div>
  );
}
